import { Slot } from "@radix-ui/react-slot"
import clsx from "clsx"
import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
  useContext,
} from "react"

import type { SX, ThemeKeys } from "../../theme"
import { Icon, type IconName } from "../Icon"
import { Text, textVariantMapping } from "../Text"
import styles from "./ActionList.module.css"

/**
 * @internal
 * Action List context used to propagate the border variant
 * for all items in the Action List.
 */
type ActionListContext = { variant: ActionListVariant }

const ActionListContext = createContext<ActionListContext>({
  variant: "bordered",
})

function useActionList() {
  return useContext(ActionListContext)
}

export type ActionListVariant = "borderless" | "bordered" | "compact"

export interface ActionListProps extends PropsWithChildren {
  variant?: ActionListVariant
  gap?: 0 | 4 | 8
  sx?: SX
}

export function ActionList(props: ActionListProps) {
  const { variant = "bordered", gap = 8, sx, ...otherProps } = props
  return (
    <ActionListContext.Provider value={{ variant }}>
      <div
        {...otherProps}
        style={sx}
        className={clsx(
          styles.root,
          variant === "bordered" && styles.rootBorder,
          gap && styles[`gap-${gap}`],
        )}
      />
    </ActionListContext.Provider>
  )
}

type Color = "grey" | "tomato"
export type ActionListItemProps = {
  children: ReactNode
  color?: Color
  backgroundColor?: "grey" | "transparent"
  description?: ReactNode
  disabled?: boolean
  selected?: boolean
  onClick?: (event: MouseEvent) => void
  renderEndIcon?: (icon: ReactNode) => ReactNode
  renderStartIcon?: (icon: ReactNode) => ReactNode
  startIcon?: IconName
  readOnly?: boolean
  textVariant?: keyof typeof textVariantMapping
  /**
   * For progress step use-case,
   * mandatory to pass the boolean value for each item, even if `false`.
   */
  completed?: boolean
  /**
   * Change the default rendered element for the one passed as a child,
   * merging their props and behavior.
   * @default false
   */
  asChild?: boolean
  /**
   * Override the variant from ActionList
   */
  variant?: ActionListVariant
  /**
   * If true, the item title and description will be rendered on a single line.
   */
  noWrap?: boolean
} & ActionListItemEndProps

export type ActionListItemEndProps =
  | { endAdornment?: ReactNode; endIcon?: IconName; endOverlay?: never }
  | {
      endAdornment?: never
      endIcon?: never
      /**
       * An overlay element that will be rendered on top of the item at the end.
       * Useful when you cannot use the `endAdornment` prop,
       * ie. when you have to render a button on the right and the ActionListItem itself is a Link.
       * (you cannot nest buttons inside links in HTML, but you can display them on top of the link)
       */
      endOverlay: ReactNode
    }

export const ActionListItem = forwardRef<HTMLDivElement, ActionListItemProps>(
  function ActionListItem(props, ref) {
    const { variant: contextVariant } = useActionList()
    const {
      children,
      color = "grey",
      backgroundColor = "grey",
      description,
      disabled,
      selected = false,
      endAdornment,
      endOverlay,
      endIcon,
      renderEndIcon = (icon) => icon,
      renderStartIcon = (icon) => icon,
      startIcon,
      completed,
      readOnly = false,
      variant = contextVariant,
      textVariant = mapItemToTextVariant[variant],
      asChild = false,
      noWrap = false,
      ...otherProps
    } = props
    const hasStartIcon = Boolean(props.renderStartIcon || props.startIcon)

    const textColor = mapColorToTheme[selected ? "purple" : color]

    const Component = asChild ? Slot : "div"
    const childrenOrGrandchildren = getChildren(children, asChild)

    return (
      <Relative hasOverlay={Boolean(endOverlay)}>
        <Component
          {...otherProps}
          className={clsx(
            styles.item,
            styles[`item-${color}`],
            styles[`item-bg-${backgroundColor}`],
            styles[`item-${variant}`],
            selected && styles.selected,
            disabled && styles.disabled,
            readOnly && styles.readOnly,
          )}
          ref={ref}
        >
          <SlotChild asChild={asChild} externalChildren={children}>
            {typeof completed === "boolean" && (
              <StepMarker completed={completed} />
            )}

            {hasStartIcon && (
              <div
                className={clsx(
                  styles.startIcon,
                  variant === "bordered" && styles.iconBorder,
                )}
              >
                {renderStartIcon(
                  startIcon && (
                    <Icon
                      name={startIcon}
                      size={variant === "compact" ? "medium" : "large"}
                    />
                  ),
                )}
              </div>
            )}

            <div
              className={clsx(
                !noWrap && styles.iconText,
                noWrap && styles["iconText-noWrap"],
              )}
            >
              <Text
                component="span"
                color={textColor.name}
                variant={textVariant}
                noWrap={noWrap}
              >
                {childrenOrGrandchildren}
              </Text>

              <Text
                component="span"
                color={textColor.description}
                variant="small"
              >
                {description}
              </Text>
            </div>

            {renderEndIcon(endIcon && <Icon name={endIcon} size="small" />)}

            {endAdornment}
          </SlotChild>
        </Component>

        <Slot className={styles.endOverlay}>{endOverlay}</Slot>
      </Relative>
    )
  },
)

interface RelativeProps {
  children: ReactNode
  hasOverlay: boolean
}

/**
 * Wraps the children in a relative parent element,
 * so that the overlay can be positioned relatively to it.
 */
function Relative(props: RelativeProps) {
  const { children, hasOverlay } = props

  // If there's no overlay, don't wrap the children in a relative parent.
  if (!hasOverlay) return <>{props.children}</>

  return <div className={styles.relativeOverlay}>{children}</div>
}

interface SlotChildProps {
  children: ReactNode
  /**
   * Children passed by the user.
   */
  externalChildren?: ReactNode
  asChild: boolean
}

/**
 * This component is used to properly render Slot's children.
 */
export const SlotChild = forwardRef<Element, SlotChildProps>(
  function SlotChild(props, ref) {
    const { externalChildren, children, asChild, ...slotProps } = props

    if (!asChild) return <>{children}</>

    // Just asserting the type, should not exit there.
    if (
      !isValidElement<PropsWithChildren<{ ref: Ref<Element> }>>(
        externalChildren,
      )
    )
      return <>{children}</>

    // Constructs a new VNode so that it gets consumed by Slot correctly:
    // (1) is an element passed by the user to be rendered instead of the default (ie. a Link element), keep in mind it's an element WITH props
    // (2) props from a Slot element are getting merged into props of the element passed by the user
    // (3) assign children passed to SlotChild to the element passed by the user
    return cloneElement(
      externalChildren, // (1)
      { ...slotProps, ref }, // (2)
      children, // (3)
    )
  },
)

/**
 * Returns children or grandchildren, depending on the `asChild` flag.
 */
function getChildren(children: ReactNode, asChild: boolean) {
  if (!asChild) return children

  // Just asserting the type, should not exit there.
  if (!isValidElement<PropsWithChildren>(children)) return undefined

  // If an element was passed to replace the default <div>, use its own children.
  return children.props.children
}

export function ActionListLabel(props: PropsWithChildren) {
  const { children } = props

  return (
    <Text
      color="grey11"
      component="span"
      sx={{ marginBottom: 12 }}
      variant="smallBold"
    >
      {children}
    </Text>
  )
}

export function ActionListHeader(props: PropsWithChildren) {
  const { children } = props

  return <div className={styles.header}>{children}</div>
}

interface StepMarkerProps {
  completed: boolean
}

/**
 * @private
 */
function StepMarker(props: StepMarkerProps) {
  const { completed } = props

  return (
    <div className={styles.stepMarker}>
      <span
        className={clsx(styles.stepMarkerCircle, completed && styles.completed)}
        aria-label={completed ? "step completed" : "step not completed"}
      />
    </div>
  )
}

const mapItemToTextVariant: Record<
  ActionListVariant,
  keyof typeof textVariantMapping
> = {
  borderless: "bold",
  bordered: "smallBold",
  compact: "normal",
}

interface TextColors {
  name: ThemeKeys<"color">
  description: ThemeKeys<"color">
}

// Extend the Color type with "purple" for the selected item UI
type ExtendedColor = Color | "purple"

const mapColorToTheme: Record<ExtendedColor, TextColors> = {
  grey: { name: "grey12", description: "grey11" },
  tomato: { name: "tomato11", description: "tomato11" },
  purple: { name: "purple11", description: "purple11" },
}
